import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { api } from '@/api/client';
import type { Model } from '@litellm-ui/shared';

interface Message {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export function PlaygroundPage() {
  const { data: models } = useQuery({
    queryKey: ['models'],
    queryFn: () => api<Model[]>('/api/models'),
  });

  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState(1);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'system', content: 'You are a helpful assistant.' },
    { role: 'user', content: '' },
  ]);
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);

  async function send() {
    if (!model) return;
    setLoading(true);
    setResponse('');

    try {
      const res = await fetch('/api/playground/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model,
          messages,
          temperature,
          max_tokens: maxTokens,
          stream: true,
        }),
      });

      if (!res.ok || !res.body) {
        setResponse(`Error: ${res.statusText}`);
        setLoading(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const payload = line.slice(6).trim();
            if (payload === '[DONE]') continue;
            try {
              const json = JSON.parse(payload);
              const delta = json.choices?.[0]?.delta?.content;
              if (delta) setResponse((r) => r + delta);
            } catch {
              // skip non-JSON lines
            }
          }
        }
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Playground</h1>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label="Model">
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              style={{
                height: 36,
                padding: '0 12px',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                background: '#fff',
                fontSize: 14,
                width: '100%',
              }}
            >
              <option value="">Select model...</option>
              {models?.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id}
                </option>
              ))}
            </select>
          </Field>

          <Field label={`Temperature: ${temperature}`}>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={temperature}
              onChange={(e) => setTemperature(parseFloat(e.target.value))}
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="Max tokens">
            <input
              type="number"
              value={maxTokens}
              onChange={(e) => setMaxTokens(parseInt(e.target.value))}
              style={{
                height: 36,
                padding: '0 12px',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                background: '#fff',
                fontSize: 14,
                width: '100%',
              }}
            />
          </Field>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Messages</label>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <select
                  value={msg.role}
                  onChange={(e) => {
                    const updated = [...messages];
                    updated[i] = { ...msg, role: e.target.value as Message['role'] };
                    setMessages(updated);
                  }}
                  style={{
                    height: 32,
                    padding: '0 8px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontSize: 12,
                  }}
                >
                  <option value="system">system</option>
                  <option value="user">user</option>
                  <option value="assistant">assistant</option>
                </select>
                <textarea
                  value={msg.content}
                  onChange={(e) => {
                    const updated = [...messages];
                    updated[i] = { ...msg, content: e.target.value };
                    setMessages(updated);
                  }}
                  rows={4}
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontSize: 13,
                    fontFamily: 'inherit',
                  }}
                />
              </div>
            ))}
          </div>

          <Button onClick={send} disabled={loading || !model}>
            {loading ? 'Sending...' : 'Send'}
          </Button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Response</label>
          <div
            style={{
              minHeight: 400,
              padding: 16,
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}
          >
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                fontSize: 13,
                margin: 0,
                fontFamily: 'inherit',
              }}
            >
              {response || '...'}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}
